import React, { useMemo } from 'react';
import DownloadIcon from '@mui/icons-material/Download';
import { translate } from '../../../../../../../utils/translation/translation';
import MenuItem from '../../MenuItem/MenuItem';
import { useRecoilValue } from 'recoil';
import { translationsState } from '../../../../../../../atoms/translation/translation.atom';
import { useMutation } from '@tanstack/react-query';
import axios, {
  ErrorResponse,
} from '../../../../../../../libraries/axios/axios';
import { apiRoutes } from '../../../../../../../../config/api/apiRoutes';
import { saveAs } from 'file-saver';
import { FileContent } from '../../../../../../Input/AttachmentInput/AttachmentBox/AttachmentBox';
import { HttpMethod } from '../../../../../../../domain/Role';
import { hasRoleAccess } from '../../../../../../../utils/access/access';
import { roleAccessState } from '../../../../../../../atoms/auth/auth.atom';

export type PdfPreviewActionAccess = {
  url: string;
  method: HttpMethod;
};

export type DownloadActionProps = {
  id?: string;
  valueKey?: string;
  label?: string;
  apiUrl?: string;
  access?: PdfPreviewActionAccess;
  isGeneric?: boolean;
};

export const DownloadAction = ({
  id,
  label,
  apiUrl,
  access,
}: DownloadActionProps) => {
  const translations = useRecoilValue(translationsState);
  const roleAccessValue = useRecoilValue(roleAccessState);

  const { mutate: downloadFile, isPending: pendingDownload } = useMutation<
    { data: FileContent },
    ErrorResponse
  >({
    mutationFn: () => {
      return axios.get(
        (apiUrl ?? apiRoutes.assets.downloadFile).replace(
          ':id',
          id?.toString() ?? '',
        ),
      );
    },
    onSuccess: (response) => {
      const downloadFileContent = response.data;

      if (!downloadFileContent || !downloadFileContent.fileBuffer) {
        return;
      }

      saveAs(
        new Blob([new Uint8Array(downloadFileContent.fileBuffer.data)], {
          type: downloadFileContent.contentType,
        }),
        downloadFileContent.filename,
      );
    },
  });

  const isDisabled = useMemo(() => {
    if (access) {
      return !hasRoleAccess(roleAccessValue, access.method, access.url);
    }

    return false;
  }, [access]);

  const handleClick = (e: any) => {
    e.stopPropagation();
    downloadFile();
  };

  return (
    <>
      <MenuItem onClick={handleClick} disabled={pendingDownload || isDisabled}>
        <DownloadIcon width={20} height={20} />
        {label ?? translate(translations, 'DOWNLOAD_ACTION.DOWNLOAD')}
      </MenuItem>
    </>
  );
};

export default DownloadAction;
